import axios from "axios";

// Backend base URL. The Emergent preview URL that shipped in .env is not a
// Novus server, so anything pointing at emergentagent.com falls back to the
// local Novus API (Backend/, `npm run dev`, port 5000).
// A production build made where no .env exists (the server builds from git, and .env is
// git-ignored) must still reach the hosted API, not each user's own localhost; the 2026-10-09
// server build did exactly that. Dev builds keep the local API.
const envUrl = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_URL = process.env.NODE_ENV === "production" ? "https://api.novushealthlabs.com" : "http://localhost:5000";
export const BACKEND_URL =
  envUrl && !/emergentagent\.com/.test(envUrl) ? envUrl.replace(/\/$/, "") : DEFAULT_URL;

// A stored picture's address (the API returns /api/v1/public/... paths) on the backend origin.
export const absolute = (path) => (!path ? "" : /^https?:/.test(path) ? path : BACKEND_URL + path);

export const TOKEN_KEY = "novus_token";
export const SESSION_KEY = "novus_session";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api/v1`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    if (status === 401 && !url.includes("/auth/login")) {
      // Token expired or revoked: drop the session and let AppContext react.
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(SESSION_KEY);
      window.dispatchEvent(new Event("novus:logout"));
    }
    return Promise.reject(err);
  }
);

// Every Novus endpoint answers { success, data }. These helpers return `data`.
export const get = async (url, params) => (await api.get(url, { params })).data.data;
export const post = async (url, body) => (await api.post(url, body)).data.data;
export const patch = async (url, body) => (await api.patch(url, body)).data.data;
export const put = async (url, body) => (await api.put(url, body)).data.data;
export const del = async (url, data) => (await api.delete(url, data ? { data } : undefined)).data.data; // data: a body, e.g. where a deleted center's records move

// Human-readable message from an API error (Zod validation details included).
export function errMsg(e) {
  const body = e?.response?.data;
  if (body?.details) {
    const d = body.details;
    if (Array.isArray(d)) {
      // Validation details carry a message per field. Other details are machine
      // codes (CREDIT_LIMIT_EXCEEDED, PAYMENT_REQUIRED…) with no text of their
      // own: those fall through to the server's message instead of an empty toast.
      const worded = d.filter((x) => x && typeof x.message === "string" && x.message);
      if (worded.length) {
        return worded
          .map((x) => {
            const path = Array.isArray(x.path) ? x.path.join(".") : x.path;
            return path ? `${path}: ${x.message}` : x.message;
          })
          .join(" · ");
      }
    }
    if (typeof d === "string") return d;
  }
  if (body?.message) return body.message;
  if (e?.code === "ERR_NETWORK") return `Cannot reach the Novus API at ${BACKEND_URL}. Is the backend running?`;
  return e?.message || "Request failed";
}

// Streams a protected PDF (needs the Bearer header, so a plain <a href> won't do)
// and opens it in a new tab.
// A PDF may be drawn on the spot (a report's first full copy): 2 minutes, not the usual 30 s
// (2026-10-26: "timeout of 30000ms exceeded" on the full report).
export const PDF_TIMEOUT = 120_000;
export async function openPdf(url) {
  const res = await api.get(url, { responseType: "blob", timeout: PDF_TIMEOUT });
  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(blobUrl, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
